// src/dmSignoff/index.js - the Duty Manager sign-off board in admin (v133.16).
// Bundled by esbuild -> dist/dmSignoff.js (globalName "DmSignoff"), loaded in admin.html, drawn into
// #page-dmsignoff (People group, beside Induction).
//
// ALGT's "Duty Manager Sign Off - Middle East" booklet: nine modules, each signed off by an area manager
// (their own area) or Head Office, here; then the final certification: the employee and their store
// manager sign on the till (My Development) with their PINs, and the area manager or Head Office signs
// last, here. Only Head Office can remove a signature. The Worker enforces all of it
// (src/worker/dm-signoff.js); this page only offers what the person may do.

let _store = '';
let _data = null;   // { ready, people, modules, headOffice }

function authHeaders() {
  const t = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const h = { 'Content-Type': 'application/json' };
  if (t) h['Authorization'] = 'Bearer ' + t;
  return h;
}
function workerBase() { return (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : ''; }
function toast(msg, kind) { if (typeof showToast === 'function') showToast(msg, kind); }
const esc = (s) => (window.escapeHtml ? window.escapeHtml(s) : String(s == null ? '' : s));
// The stores this admin may see. admin.html has no getMyStores(), so this used
// to fall back to every store in staffData and open an area manager on a store
// the Worker then refused (403). getVisibleStaff() (admin.html inline core) is
// already scoped: Head Office sees every active person, an area manager their
// area (AREA_STORE_MAP), a store manager their store. Never fall back to all.
function stores() {
  let people = null;
  try { people = (typeof getVisibleStaff === 'function') ? getVisibleStaff() : null; } catch (e) { people = null; }
  if (Array.isArray(people)) return [...new Set(people.map((s) => s && s.store).filter(Boolean))].sort();
  let scope = null;
  try { scope = (typeof getMyStores === 'function') ? getMyStores() : null; } catch (e) { scope = null; }
  if (scope && scope.length) return scope.slice().sort();
  return [];
}
function when(iso) { try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Dubai' }); } catch (e) { return ''; } }
const ROLE = { head_office: 'Head Office', area_manager: 'Area Manager', employee: 'Employee', store_manager: 'Store Manager' };

async function load() {
  const host = document.getElementById('page-dmsignoff'); if (!host) return;
  const allowed = stores();
  if (!_store || allowed.indexOf(_store) < 0) _store = allowed[0] || '';
  if (!_store) { host.innerHTML = '<div class="card"><div style="color:var(--sub);font-size:13px;padding:10px 0">No stores in your area.</div></div>'; return; }
  // Switching stores quickly: an older store's reply can arrive last. Remember
  // which store this fetch is for and drop the result if the picker has moved.
  const forStore = _store;
  let next;
  try {
    const res = await fetch(workerBase() + '/api/admin/dm-signoff?store=' + encodeURIComponent(forStore), { cache: 'no-store', headers: authHeaders() });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status));
    next = d;
  } catch (e) { next = { error: e.message || String(e) }; }
  if (forStore !== _store) return; // stale: a newer store is on screen
  _data = next;
  draw();
}
function chip(sig, label, action) {
  if (sig) {
    const rm = _data.headOffice && action ? ' <button class="btn btn-outline" style="padding:0 6px;font-size:11px" title="Remove this signature (Head Office)" onclick="' + action.remove + '">\u00d7</button>' : '';
    return '<span title="' + esc(label + ': ' + sig.by + ', ' + (ROLE[sig.role] || sig.role) + ', ' + when(sig.at)) + '" style="display:inline-block;margin:2px;padding:3px 8px;border-radius:999px;background:var(--ok-bg);color:var(--ok-deep);font-size:12px;font-weight:600">\u2713 ' + esc(label) + rm + '</span>';
  }
  if (action && action.sign) return '<button class="btn btn-outline" style="margin:2px;padding:3px 8px;font-size:12px" onclick="' + action.sign + '">Sign: <span>' + esc(label) + '</span></button>';
  return '<span style="display:inline-block;margin:2px;padding:3px 8px;border-radius:999px;background:var(--surface-3);color:var(--sub);font-size:12px">' + esc(label) + '</span>';
}
function draw() {
  const host = document.getElementById('page-dmsignoff'); if (!host) return;
  const opts = stores().map((s) => '<option' + (s === _store ? ' selected' : '') + '>' + esc(s) + '</option>').join('');
  let body;
  if (!_data) body = '<div style="color:var(--sub);font-size:13px">Loading\u2026</div>';
  else if (_data.error) body = '<div style="color:var(--red);font-size:13px">Couldn\u2019t load: ' + esc(_data.error) + '</div>';
  else if (!_data.ready) body = '<div style="color:var(--sub);font-size:13px">The Duty Manager sign-off is not set up yet: it needs database update v229-0.</div>';
  else if (!_data.people.length) body = '<div style="color:var(--sub);font-size:13px">No staff in this store.</div>';
  else {
    const mods = _data.modules || [];
    body = '<div class="table-wrap"><table class="data-table"><thead><tr><th>Name</th><th>Modules (signed by an Area Manager or Head Office)</th><th>Final certification</th></tr></thead><tbody>' +
      _data.people.map((p) => {
        const s = p.signoff, id = Number(p.id);
        const act = (item) => ({ sign: 'DmSignoff.sign(' + id + ',\'' + item + '\')', remove: 'DmSignoff.remove(' + id + ',\'' + item + '\')' });
        const modCells = mods.map(([mid, title]) => chip(s.modules[mid], title, act(mid))).join('');
        const all = s.signedModules === s.total;
        const fin = chip(s.finals.final_employee, 'Employee', { remove: act('final_employee').remove }) +
          chip(s.finals.final_store_manager, 'Store Manager', { remove: act('final_store_manager').remove }) +
          chip(s.finals.final_area, 'Area Manager or HR', (all && s.finals.final_employee && s.finals.final_store_manager) || s.finals.final_area ? act('final_area') : { remove: act('final_area').remove });
        return '<tr><td><b translate="no">' + esc(p.name) + '</b><div style="font-size:12px;color:var(--sub)">' + s.signedModules + ' of ' + s.total + ' modules' + (s.complete ? ' \u00b7 <b style="color:var(--ok-deep)">signed off</b>' : '') + '</div></td><td>' + modCells + '</td><td>' + fin + '</td></tr>';
      }).join('') + '</tbody></table></div>';
  }
  host.innerHTML = '<div class="card"><div class="section-header"><div class="section-title">\ud83e\uddba Duty Manager sign-off</div></div>' +
    '<p style="font-size:13px;color:var(--sub);margin:0 0 12px">The nine modules of the Duty Manager Sign Off booklet are signed off here by the Area Manager or Head Office. Then the employee and their Store Manager sign on the till (My Development), and the Area Manager or HR Manager signs last, here. The Virtual Store\u2019s Duty Manager course is practice for it.</p>' +
    '<div class="filter-row"><select class="filter-input" translate="no" onchange="DmSignoff.setStore(this.value)">' + opts + '</select></div>' + body + '</div>';
}
export function render() { _data = null; draw(); load(); }
export function setStore(s) { _store = s; _data = null; draw(); load(); }
export async function sign(staffId, item) {
  try {
    const res = await fetch(workerBase() + '/api/admin/dm-signoff', { method: 'POST', cache: 'no-store', headers: authHeaders(), body: JSON.stringify({ staff_id: staffId, item }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status));
    toast('Signed.', 'success');
  } catch (e) { toast('Could not sign: ' + (e.message || e), 'error'); }
  load();
}
export async function remove(staffId, item) {
  if (!window.confirm('Remove this signature? Any final signatures given after it are removed too. Only do this to correct a mistake. It is recorded in the audit log.')) return;
  try {
    const res = await fetch(workerBase() + '/api/admin/dm-signoff?staff_id=' + encodeURIComponent(staffId) + '&item=' + encodeURIComponent(item), { method: 'DELETE', cache: 'no-store', headers: authHeaders() });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status));
    toast('Signature removed.', 'success');
  } catch (e) { toast('Could not remove: ' + (e.message || e), 'error'); }
  load();
}
